import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, ScrollReveal, StatsBar, CTASection, TestimonialCard } from '../../components/UI';

const TechnicalSEO = () => {
  const auditItems = [
    { category: 'Core Web Vitals', items: ['Largest Contentful Paint (LCP)', 'First Input Delay (FID)', 'Cumulative Layout Shift (CLS)', 'Time to First Byte (TTFB)'] },
    { category: 'Crawlability', items: ['Robots.txt configuration', 'XML sitemap optimization', 'Internal linking structure', 'Crawl budget optimization'] },
    { category: 'Indexation', items: ['Index coverage issues', 'Canonical tag audit', 'Duplicate content detection', 'Noindex/nofollow audit'] },
    { category: 'On-Page Technical', items: ['Title tag optimization', 'Meta description audit', 'Header tag structure', 'Image optimization'] },
    { category: 'Site Architecture', items: ['URL structure analysis', 'Site depth evaluation', 'Navigation optimization', 'Breadcrumb implementation'] },
    { category: 'Mobile & Speed', items: ['Mobile-first indexing', 'Page speed analysis', 'Resource optimization', 'Lazy loading implementation'] },
  ];

  const features = [
    {
      icon: '🔍',
      title: 'Comprehensive Technical Audit',
      desc: 'We analyze over 200 technical ranking factors to identify every issue holding your site back. From crawl errors to Core Web Vitals, nothing escapes our audit.'
    },
    {
      icon: '⚡',
      title: 'Site Speed Optimization',
      desc: 'Page speed is a direct ranking factor. We optimize images, minify code, leverage caching, and implement lazy loading to make your site lightning fast.'
    },
    {
      icon: '📱',
      title: 'Mobile-First Optimization',
      desc: 'Google uses mobile-first indexing. We ensure your site performs flawlessly on mobile devices—responsive design, touch-friendly elements, fast mobile load times.'
    },
    {
      icon: '🏗️',
      title: 'Site Architecture',
      desc: 'A well-structured site helps both users and search engines. We optimize your URL structure, internal linking, and navigation for maximum crawlability and user experience.'
    },
    {
      icon: '📋',
      title: 'Schema Markup',
      desc: 'Structured data helps Google understand your content and can earn you rich snippets in search results. We implement relevant schema markup across your site.'
    },
    {
      icon: '🔧',
      title: 'Ongoing Monitoring',
      desc: 'Technical issues can pop up anytime. We continuously monitor your site for errors, broken links, and performance issues so problems get fixed before they hurt rankings.'
    },
  ];

  return (
    <div>
      <PageHeader 
        title="Technical SEO Services"
        subtitle="Your website's technical foundation determines how well you rank. We uncover and fix the hidden issues killing your visibility."
        breadcrumb="Technical SEO"
      />

      <StatsBar />

      {/* Why Technical SEO */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
            <ScrollReveal>
              <div>
                <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Why Technical SEO?</span>
                <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 20 }}>
                  Great Content Means Nothing If Google Can't Find It
                </h2>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  You could have the best content in your industry, but if your website has technical issues, Google won't rank it. Slow load times, crawl errors, mobile problems, and indexation issues are silent ranking killers.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 16 }}>
                  Most businesses lose 30-50% of their potential organic traffic due to technical issues they don't even know exist. Our comprehensive audits uncover these hidden problems so we can fix them.
                </p>
                <p style={{ color: '#555', fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>
                  Technical SEO is the foundation everything else is built on. Without it, your content marketing, link building, and local SEO efforts are fighting an uphill battle.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {[
                    { stat: '200+', label: 'Ranking factors analyzed' },
                    { stat: '30-50%', label: 'Traffic lost to technical issues' },
                    { stat: '53%', label: 'Users leave slow sites' },
                    { stat: '100%', label: 'Issues documented & fixed' },
                  ].map((item, i) => (
                    <div key={i} style={{ background: '#f8f9fa', borderRadius: 12, padding: 20 }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#ff6b35' }}>{item.stat}</div>
                      <div style={{ color: '#666', fontSize: 14, marginTop: 4 }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', borderRadius: 24, padding: 40 }}>
                <h3 style={{ color: 'white', fontSize: 22, fontWeight: 700, marginBottom: 24 }}>Common Technical Issues We Find</h3>
                {[
                  'Slow page load times (over 3 seconds)',
                  'Mobile usability problems',
                  'Crawl errors blocking pages from indexing',
                  'Duplicate content confusing Google',
                  'Missing or incorrect canonical tags',
                  'Broken internal and external links',
                  'Missing schema markup',
                  'Poor Core Web Vitals scores'
                ].map((problem, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <span style={{ color: '#ff6b35', fontSize: 18 }}>⚠️</span>
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15 }}>{problem}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Audit Coverage */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Audit Coverage</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                What Our Technical Audit Covers
              </h2>
              <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                We leave no stone unturned. Here's what we analyze in every technical SEO audit.
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {auditItems.map((section, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ background: 'white', borderRadius: 20, padding: 28, height: '100%', border: '1px solid #eee' }}>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #ff6b35' }}>
                    {section.category}
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {section.items.map((item, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, color: '#555', fontSize: 14 }}>
                        <span style={{ color: '#22c55e' }}>✓</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section style={{ padding: '80px 24px', background: 'white' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Services</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                What's Included
              </h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24 }}>
            {features.map((feature, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div style={{ background: '#f8f9fa', borderRadius: 20, padding: 32, height: '100%' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>{feature.icon}</div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>{feature.title}</h3>
                  <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, margin: 0 }}>{feature.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Core Web Vitals */}
      <section style={{ padding: '80px 24px', background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Core Web Vitals</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: 'white', marginTop: 10, marginBottom: 16 }}>
                Google's Page Experience Signals
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: 600, margin: '0 auto', fontSize: 17, lineHeight: 1.7 }}>
                Core Web Vitals are now a ranking factor. Here's what we optimize:
              </p>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {[
              { metric: 'LCP', name: 'Largest Contentful Paint', target: '< 2.5s', desc: 'How fast the main content loads' },
              { metric: 'FID', name: 'First Input Delay', target: '< 100ms', desc: 'How fast the page responds to interaction' },
              { metric: 'CLS', name: 'Cumulative Layout Shift', target: '< 0.1', desc: 'How stable the page layout is' },
            ].map((vital, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 20, padding: 32, textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#ff6b35', marginBottom: 8 }}>{vital.metric}</div>
                  <h4 style={{ color: 'white', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{vital.name}</h4>
                  <div style={{ background: '#22c55e', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, display: 'inline-block', marginBottom: 12 }}>
                    Target: {vital.target}
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0 }}>{vital.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={{ padding: '80px 24px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <ScrollReveal>
            <div style={{ textAlign: 'center', marginBottom: 50 }}>
              <span style={{ color: '#ff6b35', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', letterSpacing: 2 }}>Investment</span>
              <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', fontWeight: 800, color: '#1a1a2e', marginTop: 10, marginBottom: 16 }}>
                Technical SEO Packages
              </h2>
            </div>
          </ScrollReveal>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {[
              { name: 'Technical Audit', price: '$750', type: 'One-Time', desc: 'Comprehensive audit with actionable recommendations', features: ['200+ Factor Analysis', 'Prioritized Fix List', 'Competitor Comparison', 'Implementation Guide', '30-Min Strategy Call'] },
              { name: 'Technical SEO Retainer', price: '$2,000', type: '/month', desc: 'Ongoing optimization and monitoring', features: ['Initial Comprehensive Audit', 'All Fixes Implemented', 'Monthly Monitoring', 'Core Web Vitals Optimization', 'Schema Implementation', 'Monthly Reporting'], popular: true },
            ].map((plan, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div style={{ 
                  background: 'white', 
                  borderRadius: 20, 
                  padding: 36, 
                  border: plan.popular ? '2px solid #ff6b35' : '1px solid #eee',
                  position: 'relative',
                  height: '100%'
                }}>
                  {plan.popular && (
                    <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#ff6b35', color: 'white', padding: '4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      Best Value
                    </div>
                  )}
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{plan.name}</h3>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#ff6b35', marginBottom: 4 }}>{plan.price}<span style={{ fontSize: 16, color: '#888' }}>{plan.type}</span></div>
                  <div style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>{plan.desc}</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 28 }}>
                    {plan.features.map((feature, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, color: '#555', fontSize: 15 }}>
                        <span style={{ color: '#22c55e' }}>✓</span> {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className={plan.popular ? 'btn-primary' : 'btn-secondary'} style={{ display: 'block', textAlign: 'center', padding: 14, borderRadius: 50, fontSize: 15 }}>
                    Get Started
                  </Link>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection 
        title="Ready to Fix Your Technical Issues?"
        subtitle="Let's uncover what's holding your website back and fix it."
        buttonText="Get Your Free Technical Audit →"
      />
    </div>
  );
};

export default TechnicalSEO;
